/**
 * English translations for the Tourist Tools application.
 * Identical nested structure to ru.ts — enforced by TranslationKeys type.
 * Organized by feature namespace.
 */
import { TranslationKeys } from './ru';

export const EN: TranslationKeys = {
  // ============================================
  // EXISTING NAMESPACES (preserved)
  // ============================================

  // Scraped content list
  scraped: {
    // Page header
    title: 'Scraped Content',
    description: 'View and manage scraped content from data sources',

    // Column headers
    columns: {
      name: 'Name',
      type: 'Type',
      quality: 'Quality',
      status: 'Status',
      sourceUrl: 'Source URL',
      dataSource: 'Data Source',
      scraped: 'Date',
      actions: 'Actions',
      description: 'Description',
    },

    // Status badges
    status: {
      pending: 'PENDING',
      converted: 'CONVERTED',
      failed: 'FAILED',
      skipped: 'SKIPPED',
    },

    // Filter controls
    filters: {
      qualityScore: 'Quality Score',
      min: 'Min',
      max: 'Max',
      contentType: 'Content Type',
      allTypes: 'All Types',
      status: 'Status',
      allStatuses: 'All Statuses',
      pending: 'Pending',
      clearFilter: 'Clear Filter',
      clearDataSourceFilter: 'Clear Source Filter',
    },

    // Search controls
    search: {
      placeholder: 'Search by name or description...',
      clear: 'Clear Search',
    },

    // Pagination
    pagination: {
      showing: 'Showing {count} of {total}',
      previous: 'Previous',
      next: 'Next',
      page: 'Page {current} of {total}',
    },

    // Expanded row
    expanded: {
      extractedData: 'Extracted Data',
      conversionError: 'Conversion Error',
      convertedToPoi: 'Converted to POI #',
    },

    // Empty states
    empty: {
      noContent: 'No scraped content',
      noContentForSource: 'No scraped content for this source',
      sourceDeleted: 'Source deleted',
    },

    // Actions
    actions: {
      view: 'View',
      convert: 'Convert',
      converting: 'Converting...',
      expand: 'Expand',
      collapse: 'Collapse',
    },

    // Quality metrics dashboard
    metrics: {
      loadError: 'Failed to load quality metrics',
      loadFailed: 'Could not load metrics',
      totalScraped: 'Total Scraped',
      avgQualityScore: 'Avg Quality Score',
      conversionRate: 'Conversion Rate',
      byStatus: 'By Status',
      byContentType: 'By Content Type',
      byDataSource: 'By Data Source',
      sourceNumber: 'Source #{id}',
      noDataSources: 'No data sources found',
    },

    // Detail modal
    detail: {
      title: 'Scraped Content Details',
      loadError: 'Failed to load content details',
      loadFailed: 'Could not load content details',
      quality: 'Quality: {score}%',
      sourceInfo: 'Source Information',
      sourceUrl: 'Source URL',
      dataSource: 'Data Source',
      sourceNumber: 'Source #{id}',
      scrapedAt: 'Scraped At',
      convertedAt: 'Converted At',
      poiId: 'POI ID',
      conversionError: 'Conversion Error',
      extractedData: 'Extracted Data (JSON)',
    },
  },

  // Conversion messages
  conversion: {
    // Single conversion
    success: 'Successfully converted',
    failed: 'Conversion failed: {message}',
    alreadyConverted: 'Already converted',
    skipped: 'Skipped: {reason}',
    recommendation: 'Recommendation:',
    technicalDetails: 'Technical Details',

    // Quality warning modal
    qualityWarning: {
      title: 'Low Content Quality',
      message: 'Content quality ({score}%) is below the 70% threshold. Continue with conversion?',
      confirm: 'Continue',
      cancel: 'Cancel',
    },

    // Batch conversion
    batch: {
      button: 'Convert Selected ({count})',
      converting: 'Converting...',
      result: 'Conversion Result: Succeeded: {succeeded}, Failed: {failed}, Skipped: {skipped}',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      errorDetailsTitle: 'Conversion Errors',
      errorItem: 'ID {id}: {message}',
      batchId: 'Batch ID: {batchId}',
      rollback: 'Roll Back Batch',
      rollbackConfirm: 'Roll back batch {batchId}? This will delete {count} created POIs.',
      rollbackSuccess: 'Batch rolled back: {poisDeleted} POIs deleted, {contentsReset} records reset',
      rollbackFailed: 'Batch rollback failed: {message}',
      lowQualityWarning: '{count} of {total} selected items have quality below 70%. Force converting may create unreliable POIs.',
      forceConvertBatch: 'Convert All ({count})',
    },
  },

  // Common messages
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    loadingFailed: 'Failed to load content',
    retry: 'Retry',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    select: 'Select',
    selected: 'Selected',
    edit: 'Edit',
    delete: 'Delete',
    create: 'Create',
    update: 'Update',
    add: 'Add',
    actions: 'Actions',
  },

  // Content types
  contentTypes: {
    HOTEL: 'Hotel',
    ATTRACTION: 'Attraction',
    RESTAURANT: 'Restaurant',
    TRANSPORT: 'Transport',
    DESTINATION: 'Destination',
    CITY: 'City',
    TRANSPORT_HUB: 'Transport Hub',
  },

  // Map popup labels
  map: {
    address: 'Address:',
    coordinates: 'Coordinates:',
    loadingRoute: 'Loading route...',
    waypointsPlaceholder: 'Route waypoints will appear here as you chat',
  },

  // Chat messages
  chat: {
    source: 'Source:',
    title: 'Travel Assistant',
    connected: 'Connected',
    reconnecting: 'Reconnecting...',
    connecting: 'Connecting...',
    aiResponding: 'AI is responding...',
    defaultPlaceholder: 'Ask about travel destinations, hotels, activities...',
    emptyState: 'Start a conversation to plan your trip!',
    emptyHint: 'Try asking: "I want to visit the beaches of Italy for 5 days"',
    send: 'Send',
    typeMessage: 'Type your message...',
  },

  // Waypoints list
  waypoints: {
    header: 'Route Waypoints',
    count: '{count} points',
    emptyState: 'No waypoints yet. Chat with AI to add locations to your route.',
    singleWaypointHint: 'Add more waypoints to create a route.',
    removeConfirm: 'Remove "{name}" from the route?',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
  },

  // ============================================
  // NEW NAMESPACES
  // ============================================

  // Authentication
  auth: {
    login: {
      title: 'Login to system',
      username: 'Username',
      password: 'Password',
      submit: 'Sign In',
      submitting: 'Signing in...',
      error: 'Invalid username or password',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      passwordPlaceholder: 'Enter your password',
      noAccount: 'No account?',
      registerLink: 'Register',
    },
    social: {
      divider: 'Sign in via social networks',
      vk: 'Sign in with VK',
      yandex: 'Sign in with Yandex',
      mailru: 'Sign in with Mail.ru',
    },
    oauth: {
      complete: {
        title: 'One more step',
        description: 'The provider did not share your email. Please enter it to finish signing in.',
        email: 'Email',
        emailPlaceholder: 'Enter email',
        submit: 'Continue',
        submitting: 'Saving...',
        error: 'Could not complete sign-in. Please try again.',
        noPending: 'Your sign-in session expired. Please start the social sign-in again.',
      },
      errors: {
        not_configured: 'Sign-in with this provider is not available yet.',
        access_denied: 'Social sign-in was cancelled.',
        missing_code: 'The authorization service returned no code. Please try again.',
        token_exchange_failed: 'Could not reach the authorization service.',
        userinfo_failed: 'Could not fetch your profile from the provider.',
        invalid_state: 'Your sign-in session is stale. Please try again.',
        unknown_provider: 'Unknown authorization provider.',
        internal_error: 'Internal error during social sign-in.',
        default: 'Could not sign in via social network. Please try again.',
      },
    },
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    validation: {
      emailValid: 'Email is valid',
      emailInvalid: 'Invalid email format',
      passwordValid: 'Password is valid',
      passwordTooShort: 'Minimum 6 characters',
      passwordsMatch: 'Passwords match',
      passwordsMismatch: 'Passwords do not match',
    },
    register: {
      title: 'Create Account',
      email: 'Email',
      emailPlaceholder: 'Enter email',
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Repeat password',
      submit: 'Create Account',
      submitting: 'Creating account...',
      hasAccount: 'Already have an account?',
      signIn: 'Sign In',
      passwordHint: 'At least 8 characters, uppercase and lowercase letters, a number, and a special character',
      success: 'Account created! Please sign in.',
      error: 'Registration failed',
      genericError: 'An error occurred during registration',
    },
  },

  passwordReset: {
    title: 'Reset Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    submit: 'Save New Password',
    success: 'Password changed successfully! You can now log in.',
    error: {
      invalidToken: 'Invalid or already used reset link',
      expired: 'Reset link has expired. Please request a new one.',
      mismatch: 'Passwords do not match',
      tooShort: 'Password: min 8 chars with uppercase, lowercase, a digit and a special character',
      generic: 'An error occurred. Please try again later.',
    },
    requestTitle: 'Forgot Password',
    requestDescription: 'Enter your email address. We will send you a password reset link.',
    requestEmail: 'Email',
    requestSubmit: 'Send Reset Link',
    requestSuccess: 'If an account with that email exists, we have sent a password reset link.',
    backToLogin: 'Back to Login',
  },

  // Shared transport types (used in multiple components) + Phase 61 suggestion panel keys
  transport: {
    CAR: 'Car',
    TRAIN: 'Train',
    PLANE: 'Plane',
    WALKING: 'Walking',
    MIXED: 'Mixed',
    // Phase 61 — Solo "Go" mock transport suggestion panel
    panelHeading: 'Transport Options',
    demoBadge: 'Demo Mode',
    typeLabel: { train: 'Train', plane: 'Flight', bus: 'Bus' },
    bookButton: 'Book',
    bookedButton: 'Booked',
    bookSuccessToast: 'Request sent (demo)',
    bookError: 'Failed to send request. Please try again.',
    from: 'From:',
    to: 'To:',
    loadError: 'Could not load options',
    retry: 'Try again',
    empty: 'No options available for this destination.',
  },

  // Shared travel formats (used in multiple components)
  travelFormat: {
    INDIVIDUAL: 'Individual',
    GROUP: 'Group',
    MINI_GROUP: 'Mini Group',
  },

  // Route source labels
  routeSource: {
    PARTNER: 'From Organizer',
    AI_COMPOSED: 'AI Composed',
    GUIDE_AUTHORED: 'With Guide',
  },

  guideRoute: {
    badge: 'with guide',
    pricePerPerson: 'from {price}₽/pers',
    donation: 'Donate',
    guideProfileHeading: 'Your Guide',
    pricingHeading: 'Pricing',
    pricePerPersonLabel: 'Price per person',
    pricePerPersonValue: '{price}₽',
    groupSizeLabel: 'Group size',
    groupSizeValue: '{min}–{max} people',
    soloFallbackLabel: 'Solo (individual)',
    soloFallbackValue: '{price}₽',
    totalLabel: 'Total for {count} people',
    totalValue: '{price}₽',
    guidePageLink: 'Guide page →',
    ratingAriaLabel: 'Rating {value} out of 5',
    badgeAriaLabel: 'Route with guide',
  },

  // Route editing
  routeEdit: {
    editMode: 'Edit Route',
    editRoute: 'Edit',
    addPoi: 'Add Stop',
    removePoi: 'Remove',
    searchPlaceholder: 'Search points of interest...',
    noResults: 'No results found',
    tryDifferentSearch: 'Try a different search',
    undoRemove: 'Undo',
    waypointRemoved: 'Stop removed',
    minWaypointsWarning: 'Route must have at least 2 stops',
    duplicateWarning: 'This stop is already in the route',
    saveChanges: 'Save Changes',
    cancelEdit: 'Cancel',
    doneEditing: 'Done',
    dragToReorder: 'Drag to reorder',
    poiAdded: 'Stop added',
    errorSaving: 'Failed to save changes',
    searchPois: 'Search Points of Interest',
    selectToAdd: 'Select a stop to add',
    noRegionPois: 'No points of interest in this region',
    typeToFilter: 'Start typing to filter',
    publishValidation: {
      bannerTitle: 'Cannot publish — please fix:',
      saveFailed: 'Failed to save draft',
      publishFailed: 'Failed to publish route',
      loadFailed: 'Failed to load route',
      archiveNotSaved: 'Cannot archive — route has not been saved yet',
      archiveFailed: 'Failed to archive route',
      deleteFailed: 'Failed to delete route',
      waypointsMinPois: 'Route must have at least 2 stops',
      regionRequired: 'Select a region in the Details tab',
      monetizationRequired: 'Select a monetization type in Pricing tab',
      priceRequired: 'Price per person is required',
      soloPriceRequired: 'Solo price is required when solo fallback is enabled',
      titleInvalid: 'Route title must be between 3 and 120 characters',
      descriptionInvalid: 'Description must not exceed 4000 characters',
      difficultyInvalid: 'Difficulty must be Easy, Moderate, or Hard',
      estimatedDurationMinutesInvalid: 'Duration must be between 1 and 10080 minutes',
      waypointsInvalid: 'Maximum number of waypoints (100) exceeded',
    },
  },

  // Route planning form
  routes: {
    planning: {
      title: 'Plan a Route',
      destination: 'Destination',
      destinationPlaceholder: 'Enter a city or country',
      startDate: 'Start Date',
      endDate: 'End Date',
      format: 'Travel Format',
      transport: 'Transport',
      numberOfPeople: 'Number of People',
      submit: 'Create Routes',
      submitting: 'Creating routes...',
    },
    input: {
      title: 'Where are you going?',
      subtitle: 'Tell us your plans and we will find the perfect route',
      whereLabel: 'Where?',
      whenLabel: 'When?',
      formatLabel: 'Format?',
      transportLabel: 'How?',
      destinationPlaceholder: 'City or country',
      submit: 'Find Routes',
      submitting: 'Searching routes...',
    },
    card: {
      transport: 'Transport:',
      format: 'Format:',
      days: 'Days:',
      people: 'People:',
      priceOnRequest: 'Price on Request',
      routeHeading: 'Route',
      moreDetails: 'More on website',
    },
    list: {
      notFound: 'No routes found. Try adjusting your search.',
      foundCount: 'Found {count} {variants} routes',
    },
    variants: {
      title: 'Route Options',
      description: 'We found {count} options for you. Choose the best fit.',
      variantLabel: 'Option {number}',
      daysCount: '{count} days',
      routeHeading: 'Route:',
    },
    detail: {
      title: 'Route Details',
      subtitle: 'Your selected route',
      partnerRoute: 'Partner Route',
      priceOnRequest: 'Price on Request',
      datesOnRequest: 'Dates on Request',
      routeType: 'Route Type',
      format: 'Format',
      duration: 'Duration',
      peopleCount: 'Number of People',
      dates: 'Travel Dates',
      cost: 'Cost',
      keyWaypoints: 'Key Waypoints',
      activities: 'Possible Activities',
      organizer: 'Organizer',
      organizerWebsite: 'Organizer Website',
      additionalDetails: 'Additional Details',
      goToWebsite: 'Go to Route Website',
      customize: 'Add Preferences',
      mapPlaceholder: 'Route map will be available after coordinates are confirmed',
      loadingRoute: 'Loading route...',
    },
  },

  // Navigation
  navigation: {
    planning: 'Planning',
    chatAssistant: 'Chat Assistant',
    admin: 'Admin',
    profile: 'Profile',
    savedRoutes: 'Saved Routes',
    favorites: 'Favorites',
    signOut: 'Sign Out',
    signIn: 'Sign In',
    userMenu: 'User Menu',
    walkRoutes: 'Walk Routes',
    gpxImport: 'GPX Import',
    poiManagement: 'POI Management',
    guideCabinet: 'Guide Cabinet',
    aiAssistant: 'AI Assistant',
    myTrips: 'My Trips',
    darkTheme: 'Dark Theme',
    lightTheme: 'Light Theme',
    notifications: 'Notifications',
    mainNav: 'Main navigation',
    language: 'Language',
  },

  // My Trips stub page
  myTrips: {
    comingSoon: 'Coming Soon',
    comingSoonBody: 'The My Trips section is coming in a future update.',
  },

  // Walk Routes user-facing page (v1.3)
  walkRoutes: {
    title: 'Walk Routes',
    selectTerritory: 'Select Territory',
    moreComingSoon: 'More routes coming soon',
    noRoutes: 'No routes available for this territory yet',
    backToSuggestions: 'Back to Suggestions',
    distance: 'Distance',
    duration: 'Walk Duration',
    loading: 'Loading...',
    canMergeWith: 'Similar Routes',
    crossingPoint: 'Crossing Point',
    mergeButton: 'Merge',
    mergePreviewTitle: 'Merged Route Preview',
    mergeConfirm: 'Confirm Merge',
    mergeCancel: 'Cancel',
    mergeInProgress: 'Merging...',
    mergeFailed: 'Merge failed',
    mergeInfeasible: 'Merge not possible',
    mergeDistance: 'Distance',
    mergePoints: 'Points',
    generatingDescription: 'Generating description...',
    mergeSuccess: 'Routes merged successfully',
    poiBadge: 'POI',
    nearbyPois: 'Nearby Attractions',
    // Walk route comparison (v1.4 Phase 25)
    compareButton: 'Compare',
    compareBackToSuggestions: 'Back to Suggestions',
    comparisonAnalysisLoading: 'Analyzing routes with AI...',
    comparisonAnalysisError: 'Could not get comparison analysis',
    comparisonAnalysisTitle: 'Comparison Analysis',
    comparisonSelectTwo: 'Select 2 routes to compare',
    comparisonPoiCount: 'Nearby POIs',
    // Phase 26: merge recommendation badge labels
    mergeRecommendLabel: 'Merge Recommendation',
    mergeVerdictRecommend: 'Recommended to Merge',
    mergeVerdictMaybe: 'Possible, with caveats',
    mergeVerdictNotRecommend: 'Not Recommended',
    estimatedCombinedDuration: 'Total Duration:',
    // Phase 27: merge preview and save strings
    mergePreviewButton: 'Preview',
    mergeSourceComparison: 'Total',
    mergeSaveButton: 'Save to Collection',
    mergeSaving: 'Saving...',
    mergeNameLabel: 'Route Name',
    mergeNamePlaceholder: 'Enter route name',
    mergeSaveSuccess: 'Route saved to collection',
    mergeSavedRoutesLink: 'Go to Saved Routes',
    mergeLoginRequired: 'Sign in to save a route to your collection',
    mergePreviewMergeButton: 'Preview Merge',
    mergeDuplicateName: 'A route with this name already exists',
    // Phase 30: i18n gap closure — error messages, units, detail labels
    errors: {
      loadSuggestions: 'Failed to load routes. Please try again.',
      loadDetail: 'Failed to load route details.',
      loadComparison: 'Failed to load routes',
      mergePreviewLoad: 'Error loading preview',
    },
    selectPlaceholder: '— select —',
    territory: 'Territory',
    addedDate: 'Added',
    noDescription: 'No description available.',
    units: {
      km: 'km',
      min: 'min',
      hours: 'h',
    },
  },

  // Footer
  footer: {
    tagline: 'Discover the perfect trip with AI-powered planning.',
    product: 'Product',
    planning: 'Planning',
    chatAssistant: 'Chat Assistant',
    company: 'Company',
    aboutUs: 'About Us',
    contacts: 'Contact',
    copyright: 'All rights reserved.',
  },

  // Validation messages
  validation: {
    required: '{field} is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Invalid email format',
    passwordRequired: 'Password is required',
    passwordMinLength: 'Password must be at least 8 characters',
    passwordComplexity: 'Password must contain uppercase and lowercase letters, a number, and a special character',
    passwordMismatch: 'Passwords do not match',
    destinationMinLength: 'Enter a destination (at least 2 characters)',
    startDateRequired: 'Start date is required',
    startDatePast: 'Start date cannot be in the past',
    endDateRequired: 'End date is required',
    endDateAfterStart: 'End date must be after start date',
    selectTransport: 'Select at least one transport type',
    minPeople: 'Number of people must be at least 1',
    avgVisitTimeRange: 'Value must be between 1 and 480 minutes',
  },

  // Error pages and messages
  errors: {
    notFound: {
      title: 'Page Not Found',
      message: 'The page you are looking for does not exist or has been moved.',
      goHome: 'Go Home',
    },
    generic: 'An error occurred',
    loadingFailed: 'Failed to load data',
    routeCreation: 'Failed to create routes',
    routeSelection: 'Failed to select route',
  },

  // Data source detail page
  dataSourceDetail: {
    breadcrumb: {
      scrapedContent: 'Scraped Content',
      source: 'Source: {name}',
    },
    config: {
      title: 'Source Configuration',
      url: 'URL',
      status: 'Status',
      parserType: 'Parser Type',
      selectors: 'Selectors',
      cacheTtl: 'Cache (hours)',
      cronSchedule: 'Schedule',
      lastScrapedAt: 'Last Scraped',
      lastSuccessAt: 'Last Success',
      createdAt: 'Created',
      createdBy: 'Created By',
      noSelectors: 'No selectors',
    },
    loading: 'Loading data source...',
    loadError: 'Failed to load data source',
    notFound: 'Data source not found',
    contentTitle: 'Content from this source',
  },

  // Admin section
  admin: {
    // Page header
    title: 'Admin Panel',

    // Tab labels
    tabs: {
      users: 'User Management',
      dataSources: 'Data Sources',
      scrapedContent: 'Scraped Content',
      duplicates: 'Duplicates',
      conversionJobs: 'Auto-Conversion',
      transportSettings: 'Transport Settings',
    },

    // Sidebar labels
    sidebar: {
      pois: 'POIs',
      harvest: 'Harvest',
      duplicates: 'Duplicates',
      gpxRoutes: 'GPX Routes',
      transport: 'Transport',
      users: 'Users',
      guides: 'Guides',
      conversion: 'Conversion',
      poiDiscovery: 'POI Area Discovery',
      groupContent: 'Content',
      groupRoutes: 'Routes',
      groupSystem: 'System',
      collapse: 'Collapse sidebar',
      expand: 'Expand sidebar',
      duplicateBadge: '{N} duplicates pending review',
    },

    // Area-based POI discovery
    poiDiscovery: {
      pageTitle: 'POI Area Discovery',
      pageDescription: 'Draw a square area on the map and launch automatic point-of-interest discovery.',
      sectionMap: 'Search Area',
      sectionProgress: 'Job Progress',
      drawHint: 'Click and drag on the map to draw the search area.',
      sizeLabel: '{size} × {size} km',
      sizeExceeded: 'Area exceeds the maximum size ({max} km) and has been clamped.',
      discoverButton: 'Discover',
      discoverButtonLoading: 'Starting...',
      configError: 'Failed to load discovery configuration',
      startError: 'Failed to start discovery job',
      jobError: 'Discovery job error',
      statusPending: 'Pending',
      statusScraping: 'Querying providers',
      statusProcessing: 'Processing results',
      statusCompleted: 'Completed',
      statusFailed: 'Failed',
      progressCreated: 'POIs created: {count}',
      progressSkipped: 'Skipped: {count}',
      progressFailed: 'Errors: {count}',
      summaryToast: 'Discovery completed: created {created}, skipped {skipped}',
      summaryProvider: 'Providers: {summary}',
      viewPoisLink: 'View created POIs',
      accessDenied: 'Access denied. This feature is not available for your account.',
      loadingConfig: 'Loading configuration...',
    },

    // Transport config
    transportConfig: {
      title: 'Transport Radius Settings',
      description: 'Configure the coverage radius for each transport type. These values are used to filter points of interest when building routes.',
      loading: 'Loading configurations...',
      loadError: 'Failed to load transport configurations',
      notFound: 'No transport configurations found.',
      radiusLabel: 'Coverage Radius (km)',
      active: 'Active',
      inactive: 'Inactive',
      saveError: 'Save error',
      radiusUpdated: 'Radius for "{type}" updated',
      radiusMin: 'Radius must be at least {min} km',
      radiusMax: 'Radius must be no more than {max} km',
      showScrapedContent: 'Show Scraped Content',
      min: 'Min: {value} km',
      max: 'Max: {value} km',
      updated: 'Updated: {date}',
      unit: 'km',
    },

    // User management
    users: {
      title: 'Users',
      createUser: 'Create User',
      editUser: 'Edit User: {username}',
      createNewUser: 'Create New User',
      noUsers: 'No users found',
      columns: {
        username: 'Username',
        displayName: 'Display Name',
        email: 'Email',
        role: 'Role',
        status: 'Status',
        created: 'Created',
        actions: 'Actions',
      },
      status: {
        enabled: 'Active',
        disabled: 'Disabled',
      },
      roles: {
        user: 'User',
        admin: 'Administrator',
        guide: 'Guide',
      },
      form: {
        usernameLabel: 'Username *',
        usernamePlaceholder: 'Enter username (3-50 characters)',
        passwordLabel: 'Password *',
        passwordPlaceholder: 'Enter password',
        roleLabel: 'Role',
        displayNameLabel: 'Display Name',
        displayNamePlaceholder: 'Enter display name (optional)',
        emailLabel: 'Email',
        emailPlaceholder: 'Enter email (optional)',
        accountEnabled: 'Account Active',
        passwordNote: 'Note: Password changes are handled through a separate password reset procedure.',
      },
      validation: {
        usernameLength: 'Username must be between 3 and 50 characters',
        passwordRequired: 'Password is required',
        passwordComplexity: 'Password must be at least 8 characters, including uppercase and lowercase letters, a number, and a special character',
        emailInvalid: 'Invalid email format',
      },
      passwordStrength: {
        weak: 'Weak',
        medium: 'Medium',
        strong: 'Strong',
      },
      messages: {
        loadError: 'Failed to load users',
        createSuccess: 'User created successfully',
        createError: 'Failed to create user',
        updateSuccess: 'User updated successfully',
        updateError: 'Failed to update user',
        deleteConfirm: 'Are you sure you want to delete user "{username}"? This will delete the user and all their data.',
        deleteSuccess: 'User "{username}" deleted successfully',
        deleteError: 'Failed to delete user',
        cannotDeleteLastAdmin: 'Cannot delete the last administrator',
      },
    },

    // Data sources
    dataSources: {
      title: 'Data Sources',
      addDataSource: 'Add Source',
      addNewDataSource: 'Add New Data Source',
      editDataSource: 'Edit Data Source',
      noDataSources: 'No data sources configured.',
      addFirstDataSource: 'Add First Data Source',
      loading: 'Loading data sources...',
      columns: {
        url: 'URL',
        description: 'Description',
        status: 'Status',
        lastScraped: 'Last Scraped',
        lastSuccess: 'Last Success',
        createdBy: 'Created By',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
        failed: 'Failed',
      },
      time: {
        never: 'Never',
        justNow: 'Just now',
        minutesAgo: '{count}m ago',
        hoursAgo: '{count}h ago',
        daysAgo: '{count}d ago',
      },
      form: {
        basicInfo: 'Basic Information',
        urlLabel: 'URL *',
        urlReadOnly: 'URL (read only)',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'Brief description of the data source',
        statusLabel: 'Status',
        scrapingConfig: 'Scraping Configuration',
        showAdvanced: 'Show Advanced',
        hideAdvanced: 'Hide Advanced',
        parserType: 'Parser Type',
        parserJsoup: 'JSoup (Static HTML)',
        parserPlaywright: 'Playwright (JavaScript rendering)',
        cssSelectors: 'CSS Selectors',
        titleSelector: 'Title Selector',
        descriptionSelector: 'Description Selector',
        contentSelector: 'Content Selector',
        cacheTtl: 'Cache TTL (hours)',
        cronSchedule: 'CRON Schedule',
        lastError: 'Last Error',
        retryCount: 'Retry Count: {count}',
      },
      actions: {
        scrapeNow: 'Scrape Now',
        scraping: 'Scraping...',
        edit: 'Edit',
        delete: 'Delete',
      },
      messages: {
        loadError: 'Failed to load data sources',
        createSuccess: 'Data source created successfully',
        createError: 'Failed to create data source',
        updateSuccess: 'Data source updated successfully',
        updateError: 'Failed to update data source',
        deleteConfirm: 'Are you sure you want to delete data source "{url}"? This will also delete all scraped content from this source.',
        deleteSuccess: 'Data source deleted successfully',
        deleteError: 'Failed to delete data source',
        scrapeStarted: 'Scrape job started in the background',
        scrapeSuccess: 'Scrape completed successfully',
        scrapeError: 'Scrape failed: {message}',
        scrapeFailure: 'Failed to start scrape job',
        urlRequired: 'URL is required',
        urlInvalid: 'URL must start with http:// or https://',
      },
    },

    // POI Detail Page
    poi: {
      // Page header
      title: 'Point of Interest',
      loading: 'Loading POI information...',

      // Error states
      notFound: 'Point of interest not found',
      notFoundMessage: 'The POI with the given ID does not exist or has been deleted.',
      invalidId: 'Invalid POI ID',
      loadError: 'Failed to load POI information',

      // Field labels
      fields: {
        name: 'Name',
        description: 'Description',
        type: 'Type',
        region: 'Region',
        coordinates: 'Coordinates',
        latitude: 'Latitude',
        longitude: 'Longitude',
        activities: 'Activities',
        metadata: 'Additional Data',
        createdAt: 'Created',
        updatedAt: 'Updated',
        createdByLabel: 'Created by',
        updatedByLabel: 'Updated by',
        ipLabel: 'IP',
        unknownAuthor: '—',
        systemAuthor: 'System',
      },

      // Change-history timeline
      history: {
        sectionTitle: 'Change history',
        loading: 'Loading history…',
        empty: 'No changes recorded yet',
        loadError: 'Failed to load change history',
        created: 'Created',
        updated: 'Updated',
        noValue: '(empty)',
        fieldLabels: {
          name: 'Name',
          latitude: 'Latitude',
          longitude: 'Longitude',
          type: 'Type',
          region: 'Region',
          description: 'Description',
          importanceScore: 'Importance score',
          difficultyLevel: 'Difficulty',
          familyFriendly: 'Family friendly',
          accessibilityLevel: 'Accessibility',
          overnightPossible: 'Overnight possible',
          avgVisitDurationMinutes: 'Avg. visit (min)',
          activities: 'Activities',
          metadata: 'Additional data',
        },
      },

      // POI types (matching PoiType enum)
      types: {
        HOTEL: 'Hotel',
        RESTAURANT: 'Restaurant',
        LANDMARK: 'Landmark',
        MUSEUM: 'Museum',
        PARK: 'Park',
        SHOPPING: 'Shopping',
        ENTERTAINMENT: 'Entertainment',
        TRANSPORT: 'Transport',
        CITY: 'City',
        ATTRACTION: 'Attraction',
        TRANSPORT_HUB: 'Transport Hub',
        OTHER: 'Other',
      },

      // Navigation
      backToScrapedContent: 'Back to Scraped Content',
      backToAdmin: 'Back to Admin Panel',
      sourceContent: 'Source Content',
      viewSourceContent: 'View Source Content',

      // Empty/null states
      noDescription: 'No description available',
      noActivities: 'No activities listed',
      noMetadata: 'No additional data',

      // Coordinate formatting
      coordinatesFormat: '{lat}° {latDir}, {lon}° {lonDir}',
      north: 'N',
      south: 'S',
      east: 'E',
      west: 'W',
    },

    // POI Management Panel (v2.1)
    poiManagement: {
      // Tabs
      allPois: 'All POIs',
      unverified: 'Unverified',
      // Actions
      createPoi: 'Create POI',
      clearFilters: 'Clear Filters',
      edit: 'Edit',
      verify: 'Verify',
      delete: 'Delete',
      // Filters
      searchPlaceholder: 'Search by name...',
      allTypes: 'All Types',
      allStatuses: 'All Statuses',
      verifiedOnly: 'Verified',
      unverifiedOnly: 'Unverified',
      allSources: 'All Sources',
      sources: {
        manual: 'Manual',
        smart: 'Smart Create',
        geocoded: 'Geocoded',
        verificationCorrected: 'Verification Corrected',
        harvestLlm: 'Harvest (LLM)',
        territoryDiscovery: 'Territory Discovery',
        scraping: 'Scraping',
        regionAnalysis: 'AI Region Analysis',
        unknown: 'Unknown',
      },
      // Table columns
      confidence: 'Confidence',
      source: 'Source',
      actions: 'Actions',
      // Sort
      sortAsc: 'Sorted ascending',
      sortDesc: 'Sorted descending',
      // States
      loading: 'Loading\u2026',
      noPois: 'No POIs found',
      noPoisHint: 'Try adjusting your search or filter criteria.',
      loadError: 'Failed to load POIs. Check your connection and try again.',
      // Delete
      deleteConfirm: 'Delete "{name}"? This cannot be undone.',
      deleteTitle: 'Delete POI',
      deleteMessage: 'Delete "{name}"? This action cannot be undone.',
      deleting: 'Deleting\u2026',
      deleteError: 'Failed to delete POI. Please try again.',
      // Create modal
      createTitle: 'Create POI',
      smartMode: 'Smart Create',
      manualMode: 'Manual',
      smartDescription: 'Enter a name and territory. Coordinates and description will be filled in automatically. You can adjust the details before saving.',
      namePlaceholder: 'e.g. Eiffel Tower',
      territory: 'Territory',
      territoryPlaceholder: 'e.g. Paris, France',
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating\u2026',
      createError: 'Failed to create POI',
      // Smart create review flow
      saveReview: 'Save changes',
      cancelCreate: 'Close',
      discardPoi: 'Discard POI',
      reviewTitle: 'Review details',
      smartCreateSuccess: 'POI created \u2014 review the details below',
      llmDegradationNote: 'Description could not be generated \u2014 please fill in manually',
      geocodeError: 'Could not find coordinates for this location. Try a different name or use manual mode.',
      territoryResolutionError: 'Could not determine the region for the given territory. Check the spelling and try again — you can enter a city, oblast, or krai name.',
      // Edit modal
      editTitle: 'Edit POI',
      save: 'Save',
      saving: 'Saving\u2026',
      updateError: 'Failed to update POI',
      duplicateNameError: 'A POI with this name already exists in this region. Choose a different name.',
      duplicateWarning: {
        title: 'Potential duplicates',
        subtitle: (count: number) => `Found ${count} similar POI within 300 m`,
        candidateMeta: (distance: number, similarity: number) => `${distance} m • ${similarity}% similarity`,
        createAnyway: 'Create anyway',
        cancel: 'Cancel',
        creating: 'Creating…',
        ariaLabel: 'Duplicate warning',
      },
      mapClickHint: 'Click on the map to update coordinates',
      validation: {
        nameRequired: 'Name is required',
        latitudeRequired: 'Latitude is required',
        longitudeRequired: 'Longitude is required',
        typeRequired: 'Type is required',
        regionRequired: 'Region is required',
        latitudeRange: 'Latitude must be between -90 and 90',
        longitudeRange: 'Longitude must be between -180 and 180',
        latitudeInvalid: 'Enter a numeric value',
        longitudeInvalid: 'Enter a numeric value',
        duplicateName: '"{name}" already exists in {region}. Choose a different name.',
      },
      // Detail panel (Phase 39)
      detail: 'View details',
      verificationHeading: 'Verification',
      reverify: 'Re-verify coordinates',
      reverifying: 'Verifying coordinates\u2026',
      reverifyError: 'Verification failed. Check your connection and try again.',
      reverifyProgress: 'Checking coordinates via Nominatim reverse geocoding\u2026',
      reverifyResultTitle: 'Verification Result',
      reverifyStepRange: 'Coordinate range check',
      reverifyStepNullIsland: 'Null Island check',
      reverifyStepGeocode: 'Reverse geocode (Nominatim)',
      reverifyStepRegion: 'Region fuzzy match',
      reverifyPassed: 'Passed',
      reverifyFailed: 'Failed',
      reverifyThreshold: 'Threshold: \u2265 70% to verify',
      reverifyDismiss: 'Dismiss',
      reverifyRefineWithTerritory: 'Nearby territory: "{place}", use it for refinement',
      reverifyRefining: 'Refining with territory\u2026',
      reverifyStepTerritoryRefinement: 'Territory refinement ({place})',
      reverifyStepCrossCheck: 'Name-based cross-check',
      reverifyStepCrossCheckKm: 'km',
      reverifyStepCrossCheckCorrected: 'coordinates corrected',
      reverifyManualHint: 'Geocoding failed \u2014 verify coordinates manually or edit the POI.',
      reverifyEditPoi: 'Edit POI',
      descriptionSuggestionTitle: 'AI description suggestion',
      descriptionSuggestionApply: 'Apply',
      descriptionSuggestionDismiss: 'Dismiss',
      descriptionSuggestionApplied: 'Description updated',
      confidenceLabel: 'Confidence',
      sourceLabel: 'Source',
      attemptsLabel: 'Attempts',
      lastVerifiedLabel: 'Last verified',
      lastVerifiedNever: 'Never',
      detailLoadError: 'Failed to load POI details. Try again.',
      infoButton: 'View details',
      // Stats bar (Phase 39)
      statsVerified: 'Verified',
      statsUnverified: 'Unverified',
      statsTotal: 'Total',
      statsManual: 'Manual',
      statsSmart: 'Smart Create',
      statsGeocoded: 'Geocoded',
      statsError: 'Stats unavailable',
      regionPlaceholder: 'Filter by region...',
      showMap: 'Show map',
      hideMap: 'Hide map',
      // Region analysis (TASK-47)
      regionAnalysis: {
        analyzeButton: 'Analyze Region',
        analyzing: 'Analyzing...',
        error: 'Failed to analyze region. Please try again.',
        title: 'Region Analysis',
        summary: 'Summary',
        existingPois: 'existing POIs',
        byType: 'By type',
        missingTypes: 'Underrepresented types',
        suggestions: 'Suggested POIs',
        noSuggestions: 'No suggestions found.',
        addPoi: 'Add POI',
        adding: 'Adding...',
        added: 'Added',
        addError: 'Failed to add POI',
        geocodeFailed: 'Coordinates not found',
        coordsPlaceholder: '55.7558, 37.6173',
        close: 'Close',
        selectRegionHint: 'Select a region to analyze',
      },
      // Unified panel i18n (Phase 46)
      panelViewMode: 'View',
      panelEditMode: 'Edit',
      panelEdit: 'Edit',
      panelSave: 'Save',
      panelCancelEdit: 'Cancel Edit',
      panelCloseAriaLabel: 'Close panel',
      panelSelectPrompt: 'Select a POI from the table',
      panelLoadError: 'Failed to load POI data. Try selecting another row or refresh the page.',
      panelCreateMode: 'Create',
      panelCreateTitle: 'New POI',
      unsavedChangesTitle: 'Unsaved Changes',
      unsavedChangesMessage: 'You have unsaved changes. Discard them or keep editing?',
      unsavedDiscard: 'Discard',
      unsavedKeepEditing: 'Keep Editing',
      panelLoadingSkeleton: 'Loading...',
      // Phase 74 — Smart Create enrichment review screen
      sectionSeasonality: 'Seasonality & Access',
      sectionVisitInfo: 'Visit Info',
      llmUnverifiedBanner: 'Data generated by AI — review before saving',
      llmBadge: 'LLM',
      notSpecified: 'Not specified',
      fieldSeasonalAvailability: 'Seasonal Availability',
      fieldBestSeason: 'Best Season',
      fieldWeatherRisk: 'Weather Risk',
      fieldWinterAccess: 'Winter Access',
      fieldRoadAvailability: 'Road Availability',
      fieldAvgVisitTime: 'Avg Visit Time (min)',
      fieldDifficultyLevel: 'Difficulty Level',
      fieldFamilyFriendly: 'Family Friendly',
      fieldAccessibilityLevel: 'Accessibility Level',
      fieldOvernightPossible: 'Overnight Possible',
      fieldRecommendedStop: 'Recommended Stop',
      booleanYes: 'Yes',
      booleanNo: 'No',
      // Seasonality enum display values
      seasonalAvailabilityYearRound: 'Year-round',
      seasonalAvailabilitySeasonal: 'Seasonal',
      bestSeasonSummer: 'Summer',
      bestSeasonWinter: 'Winter',
      bestSeasonSpring: 'Spring',
      bestSeasonAutumn: 'Autumn',
      bestSeasonAll: 'All seasons',
      weatherRiskLow: 'Low',
      weatherRiskMedium: 'Medium',
      weatherRiskHigh: 'High',
      winterAccessOpen: 'Open',
      winterAccessLimited: 'Limited',
      winterAccessClosed: 'Closed',
      roadAvailabilityPaved: 'Paved',
      roadAvailabilityDirt: 'Dirt',
      roadAvailabilityOffroad: 'Off-road',
      roadAvailabilityNa: 'N/A',
      roadAvailabilityAllYear: 'All year',
      // Visit enum display values
      difficultyExtreme: 'Extreme',
      accessibilityFull: 'Full',
      accessibilityPartial: 'Partial',
      accessibilityLimited: 'Limited',
      accessibilityNone: 'None',
      // Phase 76 — Navigation sub-points (geometry)
      sectionNavigationPoints: 'Navigation Points',
      geometryToolbarLabel: 'Select type, then click map',
      geometryTypePARKING: 'Parking',
      geometryTypeENTRANCE: 'Entrance',
      geometryTypeVIEWPOINT: 'Viewpoint',
      geometryTypeNAVIGATION: 'Navigation',
      geometryLabelPlaceholder: 'Label (optional)',
      geometrySave: 'Save Navigation Points',
      geometrySaving: 'Saving…',
      geometryDeletePoint: 'Remove navigation point',
      geometryEmpty: 'No navigation points',
      geometryEmptyHint: 'Select a type above, then click the map to place a point',
      geometryMaxLimit: 'Maximum 20 points reached',
      geometrySaveSuccess: 'Navigation points saved',
      geometrySaveError: 'Failed to save navigation points. Check your connection and try again.',
      geometryCoordinates: 'Coordinates',
      geometryLegendAriaLabel: 'Map legend',
      geometryDragHint: 'Drag markers to reposition',
      // Phase 77 — Importance & Relevance section, verification signals, filters, columns
      sectionImportanceRelevance: 'Importance & Relevance',
      sectionVerificationSignals: 'Verification Signals',
      fieldImportanceScore: 'Importance Score',
      fieldTravelRelevance: 'Travel Relevance',
      importanceMustSee: 'Must-see',
      importanceNotable: 'Notable',
      importanceSecondary: 'Secondary',
      importanceOptional: 'Optional',
      allImportance: 'All',
      signalOsmTag: 'OSM Tourism Tag',
      signalWikimediaPhotos: 'Wikimedia Photos',
      signalReviewCount: 'Review Count',
      signalRouteFrequency: 'Route Frequency',
      signalNotChecked: 'Not checked',
      columnToggle: 'Show Columns',
      columnImportance: 'Importance',
      columnSeason: 'Season',
      columnVisitDuration: 'Visit (min)',
      filterSeason: 'Season',
      filterImportanceRange: 'Importance',
      filterFamilyFriendly: 'Family-Friendly',
      filterAll: 'All',
      filterImportanceHigh: 'High (8-10)',
      filterImportanceMedium: 'Medium (5-7)',
      filterImportanceLow: 'Low (1-4)',
      filterFamilyYes: 'Yes',
      filterFamilyNo: 'No',
      allSeasons: 'All Seasons',
      allFamilyFriendly: 'Family (all)',
      allDifficulty: 'Difficulty (all)',
      filterDifficultyEasy: 'Easy',
      filterDifficultyModerate: 'Moderate',
      filterDifficultyHard: 'Hard',
      duplicateWarningMiniMapAlt: 'Map showing proposed POI and nearby duplicates',
      notAvailable: '—',
      unitMinutes: 'min',
      unitPhotos: 'photos',
    },

    // Auto-conversion job history
    conversionJobs: {
      tabTitle: 'Auto-Conversion',
      title: 'Auto-Conversion History',
      loading: 'Loading job history...',
      error: 'Failed to load job history',
      empty: 'No auto-conversion jobs have run yet',
      retry: 'Retry',
      // Summary card
      summary: {
        title: 'Last Run',
        noRuns: 'No jobs yet',
        lastRun: 'Last Run',
        status: 'Status',
        processed: 'Processed',
        succeeded: 'Succeeded',
        failed: 'Failed',
        skipped: 'Skipped',
      },
      // Table columns
      columns: {
        status: 'Status',
        startTime: 'Start',
        endTime: 'End',
        duration: 'Duration',
        processed: 'Processed',
        succeeded: 'Succeeded',
        failed: 'Failed',
        skipped: 'Skipped',
        error: 'Error',
      },
      // Status labels
      status: {
        RUNNING: 'Running',
        SUCCESS: 'Success',
        FAILED: 'Failed',
      },
      // Duration formatting
      duration: {
        lessThanSecond: '< 1s',
        minutes: '{min}m {sec}s',
        seconds: '{sec}s',
      },
      // Pagination
      pagination: {
        previous: 'Previous',
        next: 'Next',
        page: 'Page {current} of {total}',
      },
    },

    // Duplicates management
    duplicates: {
      title: 'Detected Duplicates',
      empty: 'No duplicates detected',
      loading: 'Loading duplicates...',
      error: 'Failed to load duplicates',
      retry: 'Retry',
      scrapedContent: 'Scraped Content',
      existingPoi: 'Existing Point',
      distance: 'Distance',
      similarity: 'Name Similarity',
      typeMatch: 'Type Match',
      matchDetails: 'Match Details',
      detectedAt: 'Detected',
      actions: {
        forceConvert: 'Force Convert',
        dismiss: 'Dismiss',
        merge: 'Merge',
      },
      confirm: {
        forceConvertTitle: 'Confirm Conversion',
        forceConvertMessage: 'A separate point of interest will be created despite the detected match. Continue?',
        dismissTitle: 'Dismiss Match',
        dismissMessage: 'Dismiss the match? The content will remain in "Skipped" status.',
        mergeTitle: 'Merge Data',
        mergeMessage: 'Select fields to update on the existing point with data from the scraped content:',
        mergeFieldName: 'Name',
        mergeFieldDescription: 'Description',
        confirm: 'Confirm',
        cancel: 'Cancel',
      },
      success: {
        forceConverted: 'Content converted successfully',
        dismissed: 'Match dismissed',
        merged: 'Data merged successfully',
      },
      yes: 'Yes',
      no: 'No',
      distanceMeters: 'm',
      distanceKm: 'km',
    },
  },

  // Page-specific content
  pages: {
    routePlanner: {
      title: 'Plan a Route',
      subtitle: 'Create the perfect travel route with AI',
      loading: 'Creating route options...',
    },
    planning: {
      heroTitle: 'Plan Your Perfect Trip',
      heroSubtitle: 'Tell us where you want to go and AI will find the best routes for you',
      loading: 'Finding the best routes for you...',
      newSearch: 'Start a New Search',
    },
    home: {
      heroTitle: 'Discover Your Perfect Journey',
      heroDescription: 'Let AI help you create unforgettable travel experiences. From hidden gems to popular destinations — we craft routes just for you.',
      startPlanning: 'Start Planning',
      signInAndStart: 'Sign In and Start',
      createAccount: 'Create Account',
      whyChoose: 'Why Choose TouristTools?',
      featureSmartPlanning: 'Smart Route Planning',
      featureSmartPlanningDesc: 'AI analyzes thousands of destinations to build the perfect route based on your preferences and travel style.',
      featureWorldwide: 'Worldwide Destinations',
      featureWorldwideDesc: 'Explore routes across different regions with local highlights, cultural attractions, and hidden gems.',
      featureChatAssistant: 'Chat Assistant',
      featureChatAssistantDesc: 'Get personalized recommendations and answers to your travel questions from our smart assistant.',
      readyToStart: 'Ready to Start Your Adventure?',
      readyToStartDesc: 'Join thousands of travelers planning perfect trips with TouristTools.',
      planTrip: 'Plan a Trip',
      start: 'Get Started',
    },
    profile: {
      title: 'Profile',
      comingSoon: 'Profile settings coming soon.',
      fio: 'Full Name',
      email: 'Email',
      language: 'Interface Language',
      timezone: 'Timezone',
      notSpecified: 'Not specified',
      editButton: 'Edit Profile',
      cancelButton: 'Cancel',
      saveButton: 'Save Changes',
      savingButton: 'Saving...',
      form: {
        lastNameLabel: 'Last Name *',
        firstNameLabel: 'First Name *',
        middleNameLabel: 'Middle Name',
        emailLabel: 'Email *',
      },
      messages: {
        loadError: 'Failed to load profile',
        saveSuccess: 'Profile updated successfully',
        saveError: 'Failed to save profile',
        conflictError: 'Profile was changed in another tab. Please refresh.',
        emailTaken: 'Email is already in use by another user',
        validationError: 'Please check the entered data',
      },
      requiredNote: 'Fields marked with an asterisk are required',
      personalDataTitle: 'Personal Data',
      nameLabel: 'Your Name',
      emailLabel: 'E-mail',
      emailVerified: 'E-mail verified',
      emailNotVerified: 'E-mail not verified',
      sidebar: {
        profileSection: 'Profile',
        personalData: 'Personal Data',
        changePassword: 'Change Password',
        myTrips: 'My Trips',
        savedRoutes: 'Saved Routes',
        archive: 'Trip Archive',
        messages: 'Messages',
        comingSoon: 'Coming Soon',
        logout: 'Sign Out',
      },
      passwordCard: {
        title: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        save: 'Save',
        saving: 'Saving...',
        success: 'Password changed successfully',
        wrongCurrent: 'Incorrect current password',
        weakPassword: 'Password does not meet security requirements',
        genericError: 'Failed to change password',
      },
    },
    // ============================================
    // CHAT LANDING (v2.0 Chat-Style Entry Point)
    // ============================================
    chatLanding: {
      greetingPersonal: 'HI, {name}!',
      greetingGeneric: 'HELLO!',
      heroHeadline: 'Where shall we travel?',
      heroDescription: 'Describe your desired route — region, difficulty, interests. AI will find ready-made routes you can compare, combine or adjust to your preferences',
      placeholder: 'E.g., Altai for 5 days by car',
      micLabel: 'Voice input (coming soon)',
      send: 'Send',
      chip1: 'Discover: Krasnodar region',
      chip2: 'Explore: Kaliningrad region',
      chip3: 'Travel to: Republic of Karelia',
      thinkingStatus1: 'Analyzing preferences...',
      thinkingStatus2: 'Finding routes...',
      thinkingStatus3: 'Almost ready...',
      cancel: 'Cancel',
      queryError: 'Failed to process query. Please try again.',
      returnToResults: 'Return to results',
      dismissBanner: 'Dismiss',
      // Route results display (Phase 34)
      routeResults: {
        tabAll: 'All',
        tabWalk: 'Walking',
        tabCar: 'Driving',
        yourQuery: 'Your query',
        editQuery: 'Edit',
        extractedParams: 'Search parameters',
        noResults: 'No routes found',
        noResultsHint: 'Try changing your query or selecting a different region',
        distance: 'km',
        day1: 'day',
        day2_4: 'days',
        day5: 'days',
        difficultyEasy: 'Easy',
        difficultyMedium: 'Medium',
        difficultyHard: 'Hard',
        walkLabel: 'Walking',
        carLabel: 'Driving',
        waypointsMore: '+{count} more',
        suggestionBanner: 'Tip',
        resultsCount: '{count} routes found',
        paramRegion: 'Region',
        paramDifficulty: 'Difficulty',
        paramDuration: 'Duration',
        paramInterests: 'Interests',
        backToResults: '← Back to results',
        openRoute: 'Route details',
        compareButton: 'Compare',
        errorTitle: 'Failed to load routes',
        errorHint: 'Check your connection and try again',
        retry: 'Retry search',
      },
    },
  },

  // Saved routes
  savedRoutes: {
    title: 'Saved Routes',
    empty: 'You have no saved routes yet',
    loading: 'Loading saved routes...',
    selectForComparison: 'Select {name} for comparison',
    waypointCount: 'stops',
    daysCount: 'days',
    savedAt: 'Saved',
    waypointsLabel: 'Stops',
    daysLabel: 'Days',
    share: 'Share',
    transport: {
      car: 'Car',
      walking: 'Walking',
      combined: 'Car + Walking',
      city: 'City',
      active: 'Active',
      family: 'Family',
    },
    confirmDelete: 'Are you sure you want to delete this route?',
    deleteSuccess: 'Route deleted',
    deleteFailed: 'Failed to delete route',
    openRoute: 'Open Route',
    deleteRoute: 'Delete',
    saveButton: 'Save Route',
    updateButton: 'Save Changes',
    modal: {
      title: 'Save Route',
      nameLabel: 'Route Name',
      namePlaceholder: 'e.g. Golden Ring Road Trip',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
    },
    success: {
      saved: 'Route saved successfully',
      updated: 'Route updated successfully',
    },
    errors: {
      nameRequired: 'Enter a route name',
      duplicateName: 'A route with this name already exists',
      saveFailed: 'Failed to save route',
      loadFailed: 'Failed to load saved routes',
    },
  },

  // Favorites
  favorites: {
    title: 'Favorites',
    tabs: {
      routes: 'Routes',
      pois: 'Attractions',
    },
    empty: {
      routes: 'You have no favorite routes yet',
      pois: 'You have no favorite attractions yet',
    },
    loading: 'Loading favorites...',
    addToFavorites: 'Add to Favorites',
    removeFromFavorites: 'Remove from Favorites',
    toggleFailed: 'Failed to update favorites',
    errors: {
      loadFailed: 'Failed to load favorites',
    },
  },

  // Route comparison
  comparison: {
    title: 'Route Comparison',
    compareRoutes: 'Compare Routes',
    selectAtLeastTwo: 'Select at least 2 routes to compare',
    maxThreeRoutes: 'You can compare up to 3 routes',
    analyzingRoutes: 'Analyzing routes with AI...',
    analysisError: 'Could not get route analysis',
    llmAnalysis: 'AI Analysis',
    keyDifferences: 'Key Differences',
    tradeoffs: 'Trade-offs',
    recommendation: 'Recommendation',
    refreshWarning: 'Comparison data was lost. Please select routes again.',
    backToSavedRoutes: 'Back to Saved Routes',
    backToPlanning: 'Back to Planning',
    routeLabel: 'Route',
    transport: 'Transport',
    duration: 'Duration',
    cost: 'Cost',
    waypoints: 'Waypoints',
    priceNotSet: 'Not set',
    selected: 'Selected',
    someRoutesDeleted: 'Some selected routes have been deleted',
    comparing: 'Comparing',
    criteriaHeader: 'Criteria',
    pointsLabel: 'Number of Points',
    pace: 'Pace',
    paceDense: 'Packed',
    paceMedium: 'Medium',
    paceCalm: 'Relaxed',
    share: 'Share',
    favorite: 'Favorite',
  },

  // Pluralization forms for common words (English uses 2-form arrays cast to 3-form)
  plurals: {
    variant: ['variant', 'variants', 'variants'] as [string, string, string],
    day: ['day', 'days', 'days'] as [string, string, string],
    person: ['person', 'people', 'people'] as [string, string, string],
    route: ['route', 'routes', 'routes'] as [string, string, string],
    place: ['place', 'places', 'places'] as [string, string, string],
  },

  // ============================================
  // GPX ADMIN (Walk Route Import - v1.3)
  // ============================================
  gpxAdmin: {
    // Territory filter dropdown
    allTerritories: 'All Territories',

    // Bulk delete button label
    deleteSelected: 'Delete Selected ({count})',

    // Confirmation dialog for bulk delete
    confirmBulkDelete: 'Delete {count} routes?',

    // Sort direction indicator labels
    sortAsc: 'Sort ascending',
    sortDesc: 'Sort descending',

    // Overlap detection
    detectOverlaps: 'Detect Overlaps',
    detecting: 'Detecting...',
    overlapsTitle: 'Route Overlaps',
    noOverlapsFound: 'No overlaps found',
    hideOverlaps: 'Hide',
    routeA: 'Route A',
    routeB: 'Route B',
    crossingPoint: 'Crossing Point',
    detectedAt: 'Detected',
    selectTerritoryFirst: 'Select a territory',
    // Region-based territory dropdown (v2.4)
    allRegions: 'All Regions',
    regionsLoading: 'Loading regions...',
    regionsLoadError: 'Failed to load regions',
    noRegionBadge: 'No region',
    noRegionTooltip: 'Territory could not be matched during migration. Edit this route to assign a region.',
    noRegionEditOption: '— No region —',
    emptyRegion: 'No routes for the selected region.',
    selectRegionFirst: 'Select a region',
    // OSRM import
    osrmImport: 'Import OSRM Route',
    osrmUrlPlaceholder: 'Paste route URL (GPX trace or OSRM directions)',
    osrmUrlHint: 'OSM trace URLs and OSRM directions URLs are supported',
    mapPicker: 'Pick on Map',
    urlPaste: 'Paste URL',
    preview: 'Preview',
    previewLoading: 'Loading preview...',
    saveRoute: 'Save Route',
    saving: 'Saving...',
    startPoint: 'Start Point',
    endPoint: 'End Point',
    clickMapStart: 'Click on the map to select the start point',
    clickMapEnd: 'Click on the map to select the end point',
    poiCount: 'Nearby Points of Interest',
    importSuccess: 'Route imported successfully',
    importFailed: 'Route import failed',
    osrmOrigin: 'OSRM',
    osmTraceOrigin: 'GPX Trace',

    // Page
    pageTitle: 'GPX Route Import',
    pollingIndicator: 'Polling for updates...',

    // Import form
    importTitle: 'Import GPX Route',
    importHint: 'Paste an OpenStreetMap trace URL or OSRM directions URL to import a walk route.',
    importPlaceholder: 'https://www.openstreetmap.org/trace/12345 or directions URL',
    importButton: 'Import',
    importing: 'Importing...',
    importStarted: 'Import started — monitoring status below.',
    importError: 'Failed to start import. Please try again.',
    urlFormatError: 'Supported: OSM trace URLs or OSRM directions URLs',
    snapDistanceLabel: 'Max road snap distance:',
    snapDistanceUnit: 'm',

    // Table headers
    headerName: 'Name',
    headerTerritory: 'Territory',
    headerStatus: 'Status',
    headerDescription: 'Description',
    headerPoints: 'Points',
    headerDistance: 'Distance (km)',
    headerOsmLink: 'OSM Link',
    headerActions: 'Actions',
    headerCreated: 'Created',

    // Import status labels
    statusPending: 'Pending',
    statusProcessing: 'Processing',
    statusCompleted: 'Completed',
    statusFailed: 'Failed',
    statusMerged: 'Merged',

    // Description status labels
    descPending: 'Pending',
    descGenerating: 'Generating...',
    descReady: 'Ready',
    descFailed: 'Failed',

    // Action buttons
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    regenerate: 'Regenerate',
    regenerating: 'Generating...',
    namePlaceholder: 'Route name',
    territoryPlaceholder: 'Territory',

    // Route labels
    unnamedRoute: 'Unnamed route',

    // Table empty/loading states
    loading: 'Loading routes...',
    emptyState: 'No routes imported yet. Paste an OSM trace URL above to get started.',
    emptyTerritory: 'No routes for the selected territory.',
    selectAll: 'Select all visible routes',

    // Toast messages
    routeDeleted: 'Route deleted.',
    deleteFailed: 'Failed to delete route.',
    saveFailed: 'Failed to save changes.',
    regenStarted: 'Description regeneration started.',
    regenFailed: 'Failed to trigger description regeneration.',
    bulkDeleted: '{count} routes deleted.',
    bulkDeleteFailed: 'Error deleting some routes.',

    // Confirm dialogs
    confirmDelete: 'Delete route "{name}"? This cannot be undone.',

    // Imported routes section
    importedRoutesTitle: 'Imported Routes',
    territoryLabel: 'Territory:',

    // Overlap detection errors
    overlapFetchFailed: 'Failed to fetch overlap detection results.',
    overlapDetectFailed: 'Failed to start overlap detection.',

    // Aria labels for accessibility
    ariaOsmUrl: 'OSM trace URL',
    ariaRegenerate: 'Regenerate description',
    ariaEdit: 'Edit route',
    ariaDelete: 'Delete route',
  },

  // ============================================
  // HARVEST (Web POI Harvester - v1.2)
  // ============================================
  harvest: {
    tabName: 'Data Collection',
    title: 'Scrape Jobs',
    createJob: 'New Job',
    territory: 'Territory',
    territoryType: 'Territory Type',
    parentRegion: 'Parent Region',
    urls: 'Source URLs',
    notes: 'Notes',
    startJob: 'Start',
    retryJob: 'Retry',
    deleteJob: 'Delete',
    exportCsv: 'Export CSV',
    exportJson: 'Export JSON',

    status: {
      PENDING: 'Pending',
      SCRAPING: 'Scraping',
      AWAITING_ADMIN: 'Awaiting Input',
      PROCESSING: 'Processing',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
    },

    urlStatus: {
      QUEUED: 'Queued',
      SCRAPING: 'Scraping',
      SCRAPED: 'Scraped',
      FAILED: 'Failed',
      ADMIN_PROVIDED: 'Provided',
      SKIPPED: 'Skipped',
      PROCESSING: 'Processing',
      PROCESSED: 'Processed',
    },

    stats: {
      totalUrls: 'Total URLs',
      processed: 'Processed',
      failed: 'Failed',
      skipped: 'Skipped',
      poiCount: 'POIs Created',
      successRate: 'Success Rate',
      tokensUsed: 'Tokens',
      processingTime: 'Processing Time',
      timeUnits: {
        ms: 'ms',
        sec: 'sec',
        min: 'min',
      },
      poiFound: 'POIs Found',
    },

    list: {
      urlColumn: 'URL',
      urlMore: '+{count} more',
      loadingUrls: 'Loading URLs...',
      urlLoadError: 'Failed to load URLs',
      cancel: 'Cancel',
      newJob: '+ New Job',
      newJobTitle: 'New Scrape Job',
      byUrl: 'By URL',
      pasteText: 'Paste Text',
      searchTerritory: 'Territory Search',
      urlLabel: 'URLs (one per line):',
      urlPlaceholder: 'https://example.com/page1\nhttps://example.com/page2',
      articleText: 'Article text:',
      articlePlaceholder: 'Paste tourist article text to extract POIs...',
      discoveryInfo: 'GigaChat will automatically compile a list of attractions for the specified territory. Enter the territory name and type below.',
      territoryLabel: 'Territory:',
      territoryPlaceholder: 'e.g. Dagestan',
      typeLabel: 'Type:',
      parentRegionLabel: 'Parent region:',
      parentRegionPlaceholder: 'e.g. Russia',
      notesLabel: 'Notes:',
      notesPlaceholder: 'Optional notes',
      creating: 'Creating...',
      createAndStart: 'Create & Start',
      loading: 'Loading...',
      emptyTitle: 'No scrape jobs yet',
      emptyHint: 'Create a new job by entering source URLs',
      headerTerritory: 'Territory',
      headerMode: 'Mode',
      headerStatus: 'Status',
      headerSources: 'Sources',
      headerPoi: 'POI',
      headerCreated: 'Created',
      headerActions: 'Actions',
      moreUrls: '+{count} more',
      sourceText: 'text',
      sourceAuto: 'auto',
      failedCount: '{count} err.',
      inputBtn: 'Input',
      retryBtn: 'Retry',
      deleteBtn: 'Delete',
      prevPage: '← Back',
      pageInfo: 'Page {page} of {total}',
      nextPage: 'Next →',
    },

    // Mode labels
    mode: {
      URL_SCRAPING: 'URL',
      TEXT_INPUT: 'Text',
      TERRITORY_DISCOVERY: 'Territory',
    },

    // POI type labels
    poiTypes: {
      HOTEL: 'Hotel',
      RESTAURANT: 'Restaurant',
      LANDMARK: 'Landmark',
      MUSEUM: 'Museum',
      PARK: 'Park',
      SHOPPING: 'Shopping Center',
      ENTERTAINMENT: 'Entertainment',
      TRANSPORT: 'Transport',
      CITY: 'City',
      ATTRACTION: 'Attraction',
      TRANSPORT_HUB: 'Transport Hub',
      OTHER: 'Other',
    },

    // Content source labels
    contentSource: {
      ADMIN_PASTE: 'Manual Input',
      ADMIN_UPLOAD: 'File Upload',
      SCRAPER: 'Auto-scraped',
    },

    // Territory types
    territoryTypes: {
      CITY: 'City',
      REGION: 'Region',
      COUNTRY: 'Country',
      DISTRICT: 'District',
      ISLAND: 'Island',
      NATIONAL_PARK: 'National Park',
      RESORT: 'Resort',
    },

    // Toast messages
    messages: {
      loadError: 'Failed to load jobs',
      createError: 'Failed to create job',
      createSuccess: 'Job created: {mode}',
      jobStarted: 'Job started',
      retrySuccess: 'Job restarted',
      retryError: 'Failed to restart',
      deleteConfirm: 'Delete job?',
      deleteSuccess: 'Job deleted',
      deleteError: 'Failed to delete',
      updateSuccess: 'Job updated',
      updateError: 'Failed to save',
      exportSuccess: '{format} export complete',
      exportError: 'Export failed',
      poiMapError: 'Failed to load POI map',
      territoryRequired: 'Territory name is required',
      urlRequired: 'Enter at least one URL',
      urlInvalidFormat: 'All URLs must start with http:// or https://',
      textRequired: 'Paste text for analysis',
    },

    // Detail page strings
    detail: {
      loadError: 'Failed to load job',
      notFound: 'Job not found',
      backToList: '← Back to Jobs',
      jobTitle: 'Job #{id}: {name}',
      typeLabel: 'Type:',
      regionLabel: 'Region:',
      createdLabel: 'Created:',
      authorLabel: 'Author:',
      languageLabel: 'Language:',
      regionPlaceholder: 'Region (optional)',
      awaitingAlert: 'Some URLs could not be scraped. Please proceed to content input.',
      goToInput: 'Go to Input',
      goToContentInput: 'Go to Content Input',
      saving: 'Saving...',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      starting: 'Starting...',
      start: 'Start',
      retry: 'Retry',
      delete: 'Delete',
      poiMapTitle: 'POI Map ({count})',
      loadingMap: 'Loading map...',
      poiListTitle: 'POI List ({count})',
      poiTableNum: '#',
      poiTableName: 'Name',
      poiTableType: 'Type',
      poiTableCoords: 'Coordinates',
      progressScraped: 'Scraped:',
      progressFailed: 'Failed:',
      progressSkipped: 'Skipped:',
      urlSectionTitle: 'URLs ({count})',
      urlTableNum: '#',
      urlTableUrl: 'URL',
      urlTableStatus: 'Status',
      urlTableSource: 'Source',
      urlTablePoi: 'POI',
      urlTableError: 'Error',
      discoveryNote: '+ {count} POIs found via automatic territory search',
      addUrlsTitle: 'Add URLs',
      addUrlsPlaceholder: 'Enter URLs (one per line)',
      notesTitle: 'Notes',
      errorTitle: 'Job Error',
    },

    contentInput: {
      title: 'Content Input',
      failedUrls: 'Problematic URLs',
      pasteLabel: 'Paste content text:',
      uploadLabel: 'Or upload a file (HTML, TXT, PDF):',
      preview: 'Preview',
      submit: 'Submit Content',
      skip: 'Skip',
      submitting: 'Submitting...',
      back: 'Back',
      urlsNeedInput: 'URLs need input',
      contentFor: 'Content for:',
      placeholder: 'Paste HTML or plain text content of the page...',
      chars: 'characters',
      loadError: 'Failed to load job',
      submitSuccess: 'Content submitted',
      submitError: 'Failed to submit content',
      uploadSuccess: 'File uploaded',
      uploadError: 'Failed to upload file',
      tooShort: 'Minimum content length is 100 characters',
      skipSuccess: 'URL skipped',
      skipError: 'Failed to skip URL',
      noSelection: 'Select a URL to enter content',
      notFound: 'Job not found',
      loading: 'Loading...',
      allResolved: 'All URLs resolved! The job continues running.',
    },
  },
  booking: {
    bookThisRoute: 'Book this route',
    stepper: {
      dates: 'Dates',
      guides: 'Guides',
      confirmation: 'Confirmation',
      payment: 'Payment',
    },
    guide: {
      addToRoute: 'Add to route',
      details: 'Details',
      skip: 'Skip',
      backToGuides: '← Back to guides',
    },
    payment: {
      payForExcursion: 'Pay for excursion',
      remainderNote: 'Remainder paid on excursion day',
      timeRemaining: 'Time remaining to pay:',
      retryPayment: 'Retry payment',
      attemptCounter: 'Attempt {n} of 2',
      errorRetry: 'Payment failed. Please try again.',
      errorFinal: 'Payment failed. Please select a new guide.',
      successHeading: 'Payment complete!',
      successBody: 'Your excursion deposit has been paid. See you on {date}!',
    },
    confirmation: {
      awaitingGuide: 'Awaiting guide confirmation',
      awaitingBody: 'The guide received your excursion request. This usually takes a few hours.',
      cancelRequest: 'Cancel request',
      rejectedBanner: 'Guide {name} declined the request. Here are alternatives.',
      expiredBanner: 'Waiting time expired. Here are alternatives.',
    },
    transport: {
      heading: 'Transport & Hotels',
      comingSoon: 'Coming soon',
    },
    notifications: {
      ariaLabel: 'Notifications',
      empty: 'No new notifications',
      markAllRead: 'Mark all as read',
      viewBooking: 'Go to booking',
      title: 'Notifications',
    },
    calendar: {
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
    },
    empty: {
      noGuides: 'No guides available for selected dates',
      noGuidesBody: 'Try adjusting your travel dates',
      noGuidesInCity: 'No guides available for selected dates in {city}. Try different dates or continue without a guide.',
      changeDates: 'Change dates',
      skipGuide: 'No guide',
    },
    error: {
      loadFailed: 'Failed to load data. Try refreshing the page.',
      invalidTransition: 'This action is not available for the current booking status.',
    },
    guideBookings: {
      title: 'Booking Requests',
      subtitle: 'New requests from tourists',
      confirm: 'Confirm',
      reject: 'Decline',
      empty: 'No new requests',
      emptyBody: 'Tourist booking requests will appear here',
      tourist: 'Tourist',
      route: 'Route',
      dates: 'Dates',
      excursionType: 'Excursion type',
      price: 'Price',
      actionError: 'Action failed. Please try again.',
    },
    admin: {
      emptyGuides: 'No guides added',
      emptyGuidesBody: "Click 'Add guide' to create the first profile",
      addGuide: 'Add guide',
      saveGuide: 'Save guide',
      discardChanges: 'Discard changes',
    },
    dateSelection: {
      startDate: 'Start date',
      endDate: 'End date',
      selectDates: 'Select travel dates',
      next: 'Next',
      mismatchWarning: 'Route is planned for {routeDays} days, but you selected {selectedDays} days. Do you want to re-optimize the route?',
      confirmReoptimize: 'Re-optimize',
      cancelReoptimize: 'Cancel',
      fixDates: 'Fix',
      startPoint: 'Route start:',
    },
  },

  persistentSearch: {
    placeholder: 'Enter a destination...',
    submit: 'Find Routes',
    validationEmpty: 'Please enter a destination',
    updating: 'Updating routes...',
    ariaLabel: 'Search query',
  },

  optimize: {
    button: 'Optimize with AI',
    optimizing: 'Optimizing...',
    bannerMessage: 'Route optimized',
    accept: 'Accept',
    revert: 'Revert',
    descriptionWarning: 'Description could not be updated -- edit manually',
    tooFewWaypoints: 'Need at least 3 waypoints to optimize',
  },

  routeDetail: {
    datesPanel: {
      heading: 'Travel Dates',
      validationError: 'Could not validate dates. Please try again.',
      mismatchUnsaved: 'Route is designed for {routeDays} days, but you selected {selectedDays} days. Confirm your choice.',
      confirmUnsaved: 'Confirm',
    },
    actionButtons: {
      goWithGuide: 'Go with Guide',
      goSolo: 'Go',
      tooltipNoDates: 'Please fill in travel dates first',
      tooltipMismatch: 'Please confirm the dates',
      tooltipValidating: 'Validating dates...',
      saving: 'Saving...',
      autoSaveError: 'Failed to save route. Please try again.',
      autoSaveName: 'Route via {region} — {date}',
    },
    distanceUnit: 'km',
    share: {
      ariaLabel: 'Share route',
      copied: 'Copied!',
    },
    waypoints: {
      expand: 'Expand',
      collapse: 'Collapse',
      empty: 'No waypoints',
    },
    calendar: {
      selectStart: 'Select start date',
    },
  },

  // ============================================
  // GROUP BOOKING (Phase 66)
  // ============================================
  group: {
    formingStatus: 'Waiting for group: {x} of {n} participants, deadline {date}',
    filledStatus: 'Group formed! Proceed to payment.',
    cancelledHeading: 'Group did not form',
    cancelledBody: 'Refund will be processed within 3-5 business days.',
    soloFallbackHeading: 'Continue without a group?',
    soloFallbackBody: 'The guide offers a private tour for \u20BD{price}.',
    soloFallbackCta: 'Continue without group',
    soloFallbackConfirm: 'Confirm — \u20BD{price}',
    soloFallbackCancel: 'Cancel',
    soloFallbackHelper: 'You will switch to private pricing at \u20BD{price}.',
  },

  // ============================================
  // MONETIZATION (Phase 66)
  // ============================================
  monetization: {
    unlockCta: 'Unlock Route',
    payUnlock: 'Pay — \u20BD{amount}',
    donateCta: 'Donate \u20BD{amount}',
    freeCtaSave: 'Take this route',
    freeCtaSaved: 'Route saved',
    paywallHeading: 'Full route available after payment',
    paywallBody: 'You see the first 2 points. Pay once to unlock all {N} points.',
    unlockSuccess: 'Route unlocked! All points are now available.',
    donationSuccess: 'Thank you for supporting the author!',
    donationPlaceholder: 'Other amount',
    donationMinError: 'Enter an amount of at least 10 \u20BD',
    paymentErrorRetry: 'Payment failed. Please try again.',
    paymentErrorFinal: 'Payment could not be processed. Contact support.',
  },

  earnings: {
    heading: 'Earnings',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    allTime: 'All time',
    unlockLabel: 'Unlocks',
    donationLabel: 'Donations',
    empty: 'No data for this period yet',
  },

  // ============================================
  // BRAND (Phase 80 — Shared UI Components)
  // ============================================
  brand: {
    logoAlt: 'My Route — AI Travel Planner',
    heroImageAlt: 'Travel landscape',
    userIconAlt: 'User profile',
  },

  // ============================================
  // ROUTE UNITS (Phase 80 — PillBadge units)
  // ============================================
  route: {
    unitKm: 'km',
    unitHours: 'h',
    unitMeters: 'm',
  },

  // Phase 76–78 — Public POI detail page strings
  poiDetail: {
    sectionNavigationPoints: 'Navigation Points',
    // Phase 78 — enrichment sections
    sectionSeasonality: 'Seasonality & Access',
    sectionVisitInfo: 'Visit Info',
    fieldSeasonalAvailability: 'Seasonal Availability',
    fieldBestSeason: 'Best season',
    fieldWeatherRisk: 'Weather risk',
    fieldWinterAccess: 'Winter access',
    fieldRoadAvailability: 'Road availability',
    fieldDuration: 'Typical visit',
    fieldDifficulty: 'Difficulty',
    fieldFamilyFriendly: 'Family-friendly',
    fieldAccessibility: 'Accessibility Level',
    fieldOvernightPossible: 'Overnight possible',
    durationMinutes: '{n} min',
    durationHours: '{h} h {m} min',
    importanceMustSee: 'Must-see',
    importanceNotable: 'Notable',
    importanceSecondary: 'Secondary',
    importanceOptional: 'Optional',
  },

  guideGroupBookings: {
    heading: 'Group Bookings',
    fillStatus: '{current} of {min} participants',
    deadline: 'Deadline: {date}',
    empty: 'No active group bookings',
  },
};
